/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { OverflowCarousel } from '@coursera-web/cds-labs';

import { breakpoints } from '@coursera/cds-core';

import type { CompactCollectionsModuleFragment } from 'bundles/front-page/__generated__/contentfulTypes';
import Section from 'bundles/front-page/components/Section';
import useLohpCollection from 'bundles/front-page/hooks/useLohpCollection';
import normalizeCompactCollectionModuleContent from 'bundles/front-page/utils/normalizeCompactCollectionModuleContent';
import CompactCollection from 'bundles/unified-common/components/compact-collection/CompactCollection';
import CompactCollectionPlaceholder from 'bundles/unified-common/components/compact-collection/CompactCollectionPlaceholder';

type PresetCollectionSetType = {
  id: string;
  collectionModuleId: string;
  presetCollectionId: string;
};

type ComplexCollectionSetType = {
  id: string;
  collectionModuleId: string;
  collectionName: string;
  facets?: Array<Array<string>>;
};

export type CompactCollectionSetType = PresetCollectionSetType | ComplexCollectionSetType;

type CompactCollectionType = {
  headline: string;
  headlineTarget: string;
  collection: CompactCollectionSetType;
};

// content and headline&collections are mutually exclusive, either pass in content or headline&collections
export type CompactCollectionsModuleRendererProps =
  | {
      headline: string;
      collections: CompactCollectionType[];
      content?: never;
      dataTestId?: string;
    }
  | {
      headline?: never;
      collections?: never;
      content: CompactCollectionsModuleFragment;
      dataTestId?: string;
    };

const styles = {
  collectionsGrid: css`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    grid-template-rows: 1fr;
    gap: calc(var(--cds-spacing-150) / 2 * 3);
    width: 100%;

    ${breakpoints.down('sm')} {
      grid-template-columns: repeat(3, minmax(343px, 1fr));
      width: auto;
    }
  `,
};

const CompactCollectionWithData = ({ collectionConfig }: { collectionConfig: CompactCollectionType }) => {
  const { headline, headlineTarget, collection } = collectionConfig;
  const isPresetCollection = 'presetCollectionId' in collection && !!collection.presetCollectionId;

  const { collection: collectionData, loading: isCollectionsLoading } = useLohpCollection({
    limit: 3,
    queryCollectionId: collection.collectionModuleId,
    additionalInput: isPresetCollection
      ? {
          preset_collection_id: collection.presetCollectionId,
        }
      : {
          collection_name: 'collectionName' in collection ? collection.collectionName : '',
          facets: 'facets' in collection ? collection.facets : undefined,
        },
  });

  if (isCollectionsLoading) return <CompactCollectionPlaceholder headline={headline} headlineTarget={headlineTarget} />;

  if (!collectionData || !collectionData.entities.length) return null;

  return (
    <CompactCollection
      headline={headline}
      headlineTarget={headlineTarget}
      collectionId={collection.id}
      collectionData={collectionData}
    />
  );
};

const CompactCollectionsModuleRenderer = ({
  headline,
  collections,
  content,
  dataTestId,
}: CompactCollectionsModuleRendererProps) => {
  let moduleHeadline;
  let moduleCollections;

  if (headline && collections.length) {
    moduleHeadline = headline;
    moduleCollections = collections;
  }

  if (content) {
    const normalizedContent = normalizeCompactCollectionModuleContent(content);
    moduleHeadline = normalizedContent?.headline;
    moduleCollections = normalizedContent?.collections;
  }

  if (!moduleCollections || !moduleCollections.length) return null;

  return (
    <Section title={moduleHeadline} dataTestId={dataTestId}>
      <OverflowCarousel css={styles.collectionsGrid}>
        {moduleCollections.map((collection) => (
          <CompactCollectionWithData key={collection.headline} collectionConfig={collection} />
        ))}
      </OverflowCarousel>
    </Section>
  );
};

export default CompactCollectionsModuleRenderer;
